import { useEffect, useState } from 'react';
import apiClient from '../../utils/axiosPetition';
import { alertaCargandoProceso, alertaGeneral } from '../../utils/alertas';
import {
    PRECIO_CONTROLES_MIN,
    PRECIO_CONTROLES_SLIDER_MAX,
    PRECIO_CONTROLES_STEP,
    PRECIO_TELE_STEP,
    getPrecioControlesRange,
    getTeleconsultaPrimeraVezRange,
    getPreciosConfigByService,
    requiereConfiguracionPrecios,
} from '../../utils/preciosCitasDoctor';

const ConfiguracionPreciosCitas = ({ doctorId, doctorEstado }) => {
    const [loading, setLoading] = useState(true);
    const [doctor, setDoctor] = useState(null);

    useEffect(() => {
        if (!doctorId) return;
        setLoading(true);
        apiClient.get(`/api/doctor/${doctorId}`)
            .then((res) => setDoctor(res.data))
            .catch((err) => {
                console.error('Error cargando doctor:', err);
                alertaGeneral({ titulo: 'Error', messageHtml: 'No se pudieron cargar los datos del profesional.', isError: true });
            })
            .finally(() => setLoading(false));
    }, [doctorId]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-24 bg-gray-100 rounded" />
            </div>
        );
    }

    if (!doctor) return null;

    if (doctorEstado === 'pendiente_epayco') {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <h3 className="text-base font-semibold text-amber-900 mb-1">Precios de consultas</h3>
                <p className="text-sm text-amber-800">
                    Configura tu botón de pago ePayco primero. Cuando el equipo confirme tu registro, podrás definir tus tarifas aquí.
                </p>
            </div>
        );
    }

    if (doctorEstado !== 'aprobado') return null;

    if (!requiereConfiguracionPrecios(doctor.service)) {
        return null;
    }

    const preciosConfig = getPreciosConfigByService(doctor.service);
    const minControl = PRECIO_CONTROLES_MIN;
    const maxControl = Math.min(PRECIO_CONTROLES_SLIDER_MAX, getPrecioControlesRange(doctor.service).max);
    const currentControl = doctor.preciosCitas?.precioControles || minControl;
    const displayControl = Math.max(minControl, Math.min(maxControl, currentControl || minControl));

    const rangeTele = getTeleconsultaPrimeraVezRange(doctor.service);
    const currentTele = doctor.preciosCitas?.teleconsultaPrimeraVez ?? Math.round((rangeTele.min + rangeTele.max) / 2 / PRECIO_TELE_STEP) * PRECIO_TELE_STEP;
    const displayTele = Math.max(rangeTele.min, Math.min(rangeTele.max, currentTele));

    const onHandleSavePrice = () => {
        const precioControl = doctor.preciosCitas?.precioControles;
        if (precioControl == null || precioControl === '') {
            alertaGeneral({ titulo: 'Error', messageHtml: 'Indica el precio de consulta de control.', isError: true });
            return;
        }
        const precioControlNum = Number(precioControl);
        if (precioControlNum < PRECIO_CONTROLES_MIN || precioControlNum > maxControl || precioControlNum % PRECIO_CONTROLES_STEP !== 0) {
            alertaGeneral({
                titulo: 'Error de validación',
                messageHtml: `El precio de control debe estar entre $${PRECIO_CONTROLES_MIN.toLocaleString('es-CO')} y $${maxControl.toLocaleString('es-CO')} en pasos de $5.000.`,
                isError: true,
            });
            return;
        }

        const defaultTele = Math.round((rangeTele.min + rangeTele.max) / 2 / PRECIO_TELE_STEP) * PRECIO_TELE_STEP;
        const precioTele = doctor.preciosCitas?.teleconsultaPrimeraVez ?? defaultTele;
        const precioTeleNum = Number(precioTele);
        if (precioTeleNum < rangeTele.min || precioTeleNum > rangeTele.max || precioTeleNum % PRECIO_TELE_STEP !== 0) {
            alertaGeneral({
                titulo: 'Error de validación',
                messageHtml: `El precio de teleconsulta primera vez debe estar entre $${rangeTele.min.toLocaleString('es-CO')} y $${rangeTele.max.toLocaleString('es-CO')} en pasos de $5.000.`,
                isError: true,
            });
            return;
        }

        const precioControlFinal = Math.max(PRECIO_CONTROLES_MIN, Math.min(maxControl, Math.round(precioControlNum / PRECIO_CONTROLES_STEP) * PRECIO_CONTROLES_STEP));
        const precioTeleFinal = Math.max(rangeTele.min, Math.min(rangeTele.max, Math.round(precioTeleNum / PRECIO_TELE_STEP) * PRECIO_TELE_STEP));

        const dataDoctor = {
            preciosCitas: {
                ...doctor.preciosCitas,
                precioControles: precioControlFinal,
                teleconsultaPrimeraVez: precioTeleFinal,
                precioTeleorientacion: doctor.preciosCitas?.precioTeleorientacion ?? preciosConfig.precioTeleorientacion,
            },
        };

        alertaCargandoProceso({
            titulo: 'Guardando precios...',
            funcionAsync: () =>
                apiClient.put(`/api/clinic/editDoctor/${doctor._id}`, dataDoctor)
                    .then((res) => {
                        if (res.status === 200) {
                            setDoctor(res.data);
                            alertaGeneral({
                                titulo: '¡Tarifas guardadas!',
                                messageHtml: `Tus tarifas se actualizaron correctamente:<ul style="text-align:left;margin:8px auto 0;display:inline-block;padding-left:18px;"><li>Teleconsulta de seguimiento: $${precioControlFinal.toLocaleString('es-CO')}</li><li>Teleconsulta inicial: $${precioTeleFinal.toLocaleString('es-CO')}</li></ul>`,
                            });
                        }
                    })
                    .catch((error) => {
                        alertaGeneral({
                            titulo: 'Error al guardar',
                            messageHtml: error.response?.data?.message || 'Error inesperado, por favor intenta de nuevo.',
                            isError: true,
                        });
                    }),
        });
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50/80 to-white shadow-sm overflow-hidden">
            <div className="px-4 py-1.5 bg-[#1f94c2ff] border-b border-[#205087ff]">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 rounded-md bg-white/20 text-white font-bold text-xs">$</span>
                    Precios de consultas
                </h3>
                <p className="text-white/90 text-xs mt-0.5">Ajusta los montos según tu especialidad (incrementos de $5.000)</p>
            </div>
            <div className="p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl bg-white border border-gray-200 p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-800 text-sm">Teleconsulta primera vez</span>
                        </div>
                        <div className="text-lg font-bold text-emerald-600 mb-1.5">
                            ${displayTele.toLocaleString('es-CO')}
                            <span className="text-xs font-normal text-gray-500 ml-1">COP</span>
                        </div>
                        <input
                            type="range"
                            min={rangeTele.min}
                            max={rangeTele.max}
                            step={PRECIO_TELE_STEP}
                            value={displayTele}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                const clamped = Math.max(rangeTele.min, Math.min(rangeTele.max, value));
                                setDoctor({ ...doctor, preciosCitas: { ...doctor.preciosCitas, teleconsultaPrimeraVez: clamped } });
                            }}
                            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                            <span>${rangeTele.min.toLocaleString('es-CO')}</span>
                            <span>${rangeTele.max.toLocaleString('es-CO')}</span>
                        </div>
                    </div>

                    <div className="rounded-xl bg-white border border-gray-200 p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-800 text-sm">Consulta de control</span>
                        </div>
                        <div className="text-lg font-bold text-[#1f94c2ff] mb-1.5">
                            ${displayControl.toLocaleString('es-CO')}
                            <span className="text-xs font-normal text-gray-500 ml-1">COP</span>
                        </div>
                        <input
                            type="range"
                            min={minControl}
                            max={maxControl}
                            step={PRECIO_CONTROLES_STEP}
                            value={displayControl}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                setDoctor({ ...doctor, preciosCitas: { ...doctor.preciosCitas, precioControles: value } });
                            }}
                            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                            <span>${minControl.toLocaleString('es-CO')}</span>
                            <span>${maxControl.toLocaleString('es-CO')}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex justify-center">
                    <button
                        type="button"
                        onClick={onHandleSavePrice}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1f94c2ff] hover:bg-[#205087ff] text-white font-semibold rounded-xl shadow-md transition-all"
                    >
                        Guardar precios
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionPreciosCitas;
